/**
 * Soft≠Live Fleet Device Identity — Live GET /devices/{id} hydrate.
 * Soft kind picker (local mutate, PATCH ignores kind) stays deleted.
 */
import { useEffect, useState } from "react";
import { html, Ico, Card, KV, Alert, Field, Input } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { safeBoardPath } from "./board-api.js";

function strOrDash(v) {
  const s = String(v == null ? "" : v).trim();
  return s || "—";
}

/**
 * @param {{
 *   deviceId: string,
 *   device: object,
 *   kinds: Record<string, { t: string, glyph: string, tone: string }>,
 *   kindOf: (k: string) => { t: string, glyph: string, tone: string },
 *   onLabel?: (label: string) => void,
 *   toast: function,
 * }} props
 */
export function DeviceIdentityLive({ deviceId, device, kinds, kindOf, onLabel, toast }) {
  const did = String(deviceId || "").trim();
  const act = useAction();
  const detailLive = useApi(did ? path("get__devices_p", { pathParams: [did] }) : "");
  const teleLive = useApi(did ? (safeBoardPath("get__devices_p_board-telemetry", { pathParams: [did] }) || "") : "");
  const detail = detailLive.data && typeof detailLive.data === "object" ? detailLive.data : null;
  const tele = teleLive.data && typeof teleLive.data === "object" ? teleLive.data : null;
  const telePayload = tele && tele.payload && typeof tele.payload === "object" ? tele.payload : {};
  const KINDS = kinds && typeof kinds === "object" ? kinds : {};
  const resolveKind = typeof kindOf === "function"
    ? kindOf
    : (k) => ({ t: k, glyph: "chip", tone: "#8CA2A6" });

  const liveLabel = detail
    ? String(detail.display_label || detail.map_label || "").trim()
    : "";
  const [label, setLabel] = useState(() => String((device && device.label) || "").trim());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) return;
    if (liveLabel) setLabel(liveLabel);
    else if (device && device.label) setLabel(String(device.label).trim());
  }, [liveLabel, device && device.label, dirty]);

  const glyphKind = (device && device.kind) || "node";
  const k = resolveKind(glyphKind);
  const fw = strOrDash((detail && detail.fw) || telePayload.fw || (device && device.fw));
  const sku = strOrDash(detail && detail.hw_code_resolved);
  const product = strOrDash((detail && detail.product_hw_code) || (device && device.product_hw_code));
  const chip = strOrDash((detail && detail.chip_target) || telePayload.chip_target);
  const board = strOrDash((detail && detail.board_profile) || (tele && tele.profile_id));
  const mac = detail ? strOrDash(detail.mac_display || detail.mac_nocolon || detail.mac) : "—";

  const reload = () => {
    detailLive.reload && detailLive.reload();
    teleLive.reload && teleLive.reload();
    toast && toast("Identity re-read · Live");
  };

  const save = async () => {
    if (!did) { toast("No device id", "warn"); return; }
    const body = { display_label: String(label || "").trim() };
    const out = await act.run(path("patch__devices_p_profile", { pathParams: [did] }), {
      method: "PATCH",
      body,
    });
    if (!out) {
      toast(act.error || "Identity write refused — nothing was sent", "bad");
      return;
    }
    setDirty(false);
    onLabel && onLabel(body.display_label);
    toast("Identity label written · Live", "go");
    reload();
  };

  const reset = () => {
    setLabel(liveLabel || String((device && device.label) || "").trim());
    setDirty(false);
  };

  return html`<div className="cmc-id-live cmc-id-live--liquid" data-cmc-page="page99-device-live">
    <${Alert} tone="info" title="Live identity"
      say="Label writes PATCH /devices/{id}/profile display_label. Hardware fields come from GET /devices/{id}. Soft kind picker (local mutate while PATCH ignores kind) stays deleted — glyph family is display only. Primary CTAs carry · Live."/>
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="btn-row cmc-id-live__tools">
      <button className="btn btn--ghost btn--sm" type="button"
        disabled=${!did || detailLive.loading}
        onClick=${reload}>
        <${Ico} d="refresh" w=${13}/>Re-read · Live</button>
      <button className="btn btn--go btn--sm" type="button"
        disabled=${!dirty || act.busy}
        onClick=${save}>
        <${Ico} d="check" w=${13}/>${act.busy ? "Writing · Live…" : "Save label · Live"}</button>
      <button className="btn btn--ghost btn--sm" type="button"
        disabled=${!dirty}
        onClick=${reset}>Reset · Live</button>
    </div>

    ${detailLive.error && !detail
      ? html`<${Alert} tone="warn" title="Live device detail unavailable"
          say=${String(detailLive.error)}/>`
      : null}

    <div className="grid g2">
      <${Card} title="Operator label" meta="writable · Live profile">
        <${Field} label="Label" hint="What operators read on the map and in every alarm.">
          <${Input} icon="chip" value=${label}
            onChange=${(e) => { setLabel(e.target.value); setDirty(true); }}/>
        <//>
        <${Field} label="Device id" hint="Immutable. Every audit entry is filed against it.">
          <${Input} icon="lock" value=${did} disabled/>
        <//>
      </${Card}>

      <${Card} title="Hardware (Live)" meta="GET /devices/{id} · board-telemetry fill when registry empty">
        <${KV} k="Firmware" v=${fw}/>
        <${KV} k="SKU / hw code" v=${sku} tone=${sku === "—" ? "dim" : "go"}/>
        <${KV} k="Product hw" v=${product} tone=${product === "—" ? "dim" : ""}/>
        <${KV} k="Chip target" v=${chip} tone=${chip === "—" ? "dim" : ""}/>
        <${KV} k="Board profile" v=${board} tone=${board === "—" ? "dim" : ""}/>
        <${KV} k="MAC" v=${mac} tone=${mac === "—" ? "dim" : ""}/>
        <p className="note">Empty means the Live frame has not reported that field yet — nothing Soft-filled.</p>
      </${Card}>
    </div>

    <${Card} title="Glyph family" meta="display only · not a profile write">
      <div className="kpick kpick--ro" aria-disabled="true">
        ${Object.entries(KINDS).map(([kk, v]) => html`
          <span key=${kk} className="kpick__chip" data-on=${glyphKind === kk ? "1" : "0"}
            style=${{ "--kc": v.tone }}>
            <${Ico} d=${v.glyph} w=${14}/><span>${v.t}</span>
          </span>`)}
        ${!KINDS[glyphKind]
          ? html`<span className="kpick__chip" data-on="1" style=${{ "--kc": k.tone }}>
              <${Ico} d=${k.glyph} w=${14}/><span>${k.t || glyphKind}</span>
            </span>`
          : null}
      </div>
      <p className="note">This console guesses a glyph from Live labels and hardware codes so the header has an icon. It is not a writable device kind — Soft click-to-mutate kind theatre is deleted.</p>
    </${Card}>
  </div>`;
}
